import React from "react";
import moment from "moment";
import { rangeDates } from "../../components/tenant-modules";
import { Chart, ChartSeries, ChartSeriesItem, ChartCategoryAxis, ChartCategoryAxisItem, ChartLegend, ChartTooltip, ChartArea } from "@progress/kendo-react-charts";

// Top Search Terms from the CLIENT SITE's own analytics endpoint — built for
// Fairfield, whose site exposes GET /api/Analytics/SearchTerms (App Insights
// request-log KQL over /api/search/suggest?q=, aggregated top-N server-side;
// key-gated + CORS'd to the Plunk origins). ~1 KB responses replace the
// generic keyvalue-leaderboard's raw-kv aggregation, which outgrew the
// keyvalues Lambda's 6 MB response cap at Fairfield's search volume.
//
// Manifest example (swap for Fairfield's keyvalue-leaderboard entry):
//   { "id": "search-terms", "component": "custom:insights-search-terms",
//     "placement": { "type": "widget", "page": "acquisition" },
//     "title": "Top Search Terms",
//     "config": { "url": "https://www.fairfieldchair.com/api/Analytics/SearchTerms",
//                 "apiKey": "<SearchAnalytics:ModuleApiKey>",
//                 "top": 10, "minLength": 4 } }
//
// The endpoint's App Insights retention is 90 days — ranges older than that
// return empty (rendered honestly). Ranges longer than 90 days truncate to
// the trailing 90 with a note.
export default class InsightsSearchTerms extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      rows: null,
      error: false,
      truncated: false
    };
    this._alive = false;
  }
  componentDidMount() {
    this._alive = true;
    this.load();
  }
  componentWillUnmount() {
    this._alive = false;
  }
  componentDidUpdate(prevProps) {
    if (prevProps.range !== this.props.range) this.load();
  }
  async load() {
    const config = this.props.config || {};
    if (!config.url || !config.apiKey) {
      this.setState({
        rows: [],
        error: true
      });
      return;
    }
    let {
      startDate,
      endDate
    } = rangeDates(this.props.range);
    // endpoint end date is INCLUSIVE (it adds the day itself) — undo the
    // keyvalues-convention +1 day from rangeDates.
    endDate = moment(endDate, "YYYY-MM-DD").subtract(1, "day").format("YYYY-MM-DD");
    let truncated = false;
    if (moment(endDate).diff(moment(startDate), "days") > 90) {
      startDate = moment(endDate).subtract(90, "days").format("YYYY-MM-DD");
      truncated = true; // App Insights retains 90 days
    }
    // Request headroom over the display count: emails typed into the
    // search box show up as terms (PII — the kv module filtered them
    // too); we drop them client-side and still fill the board.
    const want = config.top || 10;
    const qs = `start=${startDate}&end=${endDate}&top=${Math.min(want + 10, 100)}&minLength=${config.minLength || 4}`;
    try {
      const r = await fetch(`${config.url}?${qs}`, {
        headers: {
          "X-Api-Key": config.apiKey
        }
      });
      if (!r.ok) throw new Error(String(r.status));
      const data = await r.json();
      let rows = data && (data.terms || data.rows || data.result) || [];
      if (config.excludeEmails !== false) {
        rows = rows.filter(t => String(t.term != null ? t.term : t.value).indexOf("@") === -1);
      }
      rows = rows.slice(0, want);
      if (this._alive) this.setState({
        rows,
        error: false,
        truncated
      });
    } catch (e) {
      if (this._alive) this.setState({
        rows: [],
        error: true
      });
    }
  }
  render() {
    const {
      rows,
      error,
      truncated
    } = this.state;
    if (rows === null) return /*#__PURE__*/React.createElement("p", {
      className: "module-empty"
    }, "Loading\u2026");
    if (error) return /*#__PURE__*/React.createElement("p", {
      className: "module-empty"
    }, "Data unavailable.");
    if (!rows.length) return /*#__PURE__*/React.createElement("p", {
      className: "module-empty"
    }, "No search data for this range.");
    const hitsOf = r => Number(r.hits != null ? r.hits : r.count) || 0;
    const termOf = r => r.term != null ? r.term : r.value;
    // Telerik horizontal bars, kendo-palette yellow (matches the Site
    // Activity conversions line) — terms on the category axis, top on top.
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Chart, {
      style: {
        height: `${Math.max(220, rows.length * 34 + 60)}px`
      },
      transitions: false
    }, /*#__PURE__*/React.createElement(ChartArea, {
      background: "transparent"
    }), /*#__PURE__*/React.createElement(ChartLegend, {
      visible: false
    }), /*#__PURE__*/React.createElement(ChartCategoryAxis, null, /*#__PURE__*/React.createElement(ChartCategoryAxisItem, {
      categories: rows.map(termOf),
      majorGridLines: {
        visible: false
      }
    })), /*#__PURE__*/React.createElement(ChartSeries, null, /*#__PURE__*/React.createElement(ChartSeriesItem, {
      type: "bar",
      data: rows.map(hitsOf),
      color: "#ffd246",
      labels: {
        visible: true,
        position: "outsideEnd",
        background: "transparent"
      }
    })), /*#__PURE__*/React.createElement(ChartTooltip, null)), truncated && /*#__PURE__*/React.createElement("p", {
      className: "module-empty",
      style: {
        marginTop: "6px"
      }
    }, "Showing the most recent 90 days (site analytics retention)."));
  }
}